<script setup lang="ts">
import { gsap } from 'gsap'

const props = defineProps<{
  slug: string
  accent: string
}>()

const { tm, rt } = useI18n()
const localePath = useLocalePath()
const dirX = useDirX()
const section = ref<HTMLElement | null>(null)

const bullets = computed(() => (tm(`page.business_types.${props.slug}.deepdive.bullets`) as unknown[]).map(b => rt(b)))

onMounted(() => {
  const el = section.value
  if (!el) return

  const dir = dirX.value

  gsap.fromTo(el.querySelector('.dd-text'),
    { opacity: 0, x: -50 * dir },
    {
      opacity: 1, x: 0, duration: 0.85, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 78%', once: true },
    },
  )

  gsap.fromTo(el.querySelector('.dd-card'),
    { opacity: 0, x: 60 * dir, rotation: 3 * dir, scale: 0.94 },
    {
      opacity: 1, x: 0, rotation: 0, scale: 1, duration: 0.9, ease: 'power4.out',
      scrollTrigger: { trigger: el, start: 'top 78%', once: true },
    },
  )

  gsap.fromTo(el.querySelectorAll('.dd-bullet'),
    { opacity: 0, x: 24 * dir },
    {
      opacity: 1, x: 0, duration: 0.5, ease: 'power2.out', stagger: 0.1,
      scrollTrigger: { trigger: el.querySelector('.dd-card'), start: 'top 75%', once: true },
    },
  )
})
</script>

<template>
  <section ref="section" class="py-12 md:py-16 lg:py-20 bg-white relative overflow-hidden">
    <div class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true" />
    <div class="container relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div class="dd-text text-center lg:text-start">
        <h2 class="text-[clamp(1.75rem,3.5vw,2.75rem)] font-bold leading-[1.25] text-deep-indigo mb-4">
          {{ $t(`page.business_types.${props.slug}.deepdive.title`) }}
        </h2>
        <p class="text-base md:text-lg text-overcast-night leading-relaxed mb-7 max-w-lg mx-auto lg:mx-0">
          {{ $t(`page.business_types.${props.slug}.deepdive.desc`) }}
        </p>
        <a v-magnetic :href="localePath('/request-demo')" class="bfm-btn bfm-btn-md" :style="{ backgroundColor: props.accent, color: '#fff' }">
          {{ $t(`page.business_types.${props.slug}.deepdive.cta`) }}
        </a>
      </div>

      <div
        class="dd-card bg-white border-2 border-gray-900 rounded-2xl p-7 md:p-9"
        :style="{ boxShadow: `10px 10px 0 ${props.accent}` }">
        <ul class="flex flex-col gap-4 md:gap-5">
          <li v-for="(bullet, i) in bullets" :key="i" class="dd-bullet flex items-start gap-3">
            <span
              class="flex-shrink-0 size-6 md:size-7 rounded-md border-2 border-gray-900 shadow-[2px_2px_0_#111827] flex items-center justify-center mt-0.5"
              :style="{ backgroundColor: props.accent }">
              <UIcon name="i-heroicons-check-20-solid" class="text-white size-4" />
            </span>
            <span class="text-sm md:text-base font-medium text-gray-900 leading-relaxed">{{ bullet }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
